import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import {
    catchError,
    exhaustMap,
    filter,
    forkJoin,
    map,
    of,
    switchMap,
    withLatestFrom,
} from "rxjs";
import { GroupsApiService } from "../data-access/groups-api.service";
import { withSkeletonTiming } from "../../../shared/operators/skeleton-timing.operator";
import { GroupsActions } from "./groups.actions";
import { groupsFeature } from "./groups.reducer";

@Injectable()
export class GroupsEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly api = inject(GroupsApiService);

    readonly open$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GroupsActions.pageOpened),
            switchMap(() =>
                this.api.loadGroups().pipe(
                    map((groups) => GroupsActions.groupsLoaded({ groups })),
                    catchError(() => of(GroupsActions.groupsLoadFailed()))
                )
            )
        )
    );

    readonly selectAfterLoad$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GroupsActions.groupsLoaded),
            withLatestFrom(
                this.store.select(groupsFeature.selectSelectedGroupId)
            ),
            filter(([, groupId]) => Boolean(groupId)),
            map(([, groupId]) =>
                GroupsActions.groupSelected({ groupId: groupId! })
            )
        )
    );

    readonly loadPeople$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GroupsActions.groupSelected),
            switchMap(({ groupId }) =>
                forkJoin({
                    members: this.api.loadMembers(groupId),
                    admins: this.api.loadAdmins(groupId),
                }).pipe(
                    withSkeletonTiming(),
                    map(({ members, admins }) =>
                        GroupsActions.peopleLoaded({
                            groupId,
                            members,
                            admins,
                        })
                    ),
                    catchError(() =>
                        of(GroupsActions.peopleLoadFailed({ groupId }))
                    )
                )
            )
        )
    );

    readonly create$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GroupsActions.createRequested),
            exhaustMap(({ name }) =>
                this.api.createGroup(name).pipe(
                    map((group) => GroupsActions.createSucceeded({ group })),
                    catchError(() => of(GroupsActions.mutationFailed()))
                )
            )
        )
    );

    readonly loadCreatedGroup$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GroupsActions.createSucceeded),
            map(({ group }) =>
                GroupsActions.groupSelected({ groupId: group.gmvid })
            )
        )
    );

    readonly rename$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GroupsActions.renameRequested),
            exhaustMap(({ group, name }) => {
                const updated = { ...group, name };
                return this.api.updateGroup(updated).pipe(
                    map(() =>
                        GroupsActions.renameSucceeded({ group: updated })
                    ),
                    catchError(() => of(GroupsActions.mutationFailed()))
                );
            })
        )
    );

    readonly delete$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GroupsActions.deleteRequested),
            exhaustMap(({ groupId }) =>
                this.api.deleteGroup(groupId).pipe(
                    map(() => GroupsActions.deleteSucceeded({ groupId })),
                    catchError(() => of(GroupsActions.mutationFailed()))
                )
            )
        )
    );

    readonly selectAfterDelete$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GroupsActions.deleteSucceeded),
            withLatestFrom(
                this.store.select(groupsFeature.selectSelectedGroupId)
            ),
            filter(([, groupId]) => Boolean(groupId)),
            map(([, groupId]) =>
                GroupsActions.groupSelected({ groupId: groupId! })
            )
        )
    );

    readonly addMembers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GroupsActions.addMembersRequested),
            exhaustMap(({ groupId, usernames }) =>
                this.api.addMembers(groupId, usernames).pipe(
                    map(() =>
                        GroupsActions.peopleMutationSucceeded({ groupId })
                    ),
                    catchError(() => of(GroupsActions.mutationFailed()))
                )
            )
        )
    );

    readonly removeMembers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GroupsActions.removeMembersRequested),
            exhaustMap(({ groupId, usernames }) =>
                this.api.removeMembers(groupId, usernames).pipe(
                    map(() =>
                        GroupsActions.peopleMutationSucceeded({ groupId })
                    ),
                    catchError(() => of(GroupsActions.mutationFailed()))
                )
            )
        )
    );

    readonly addAdmins$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GroupsActions.addAdminsRequested),
            withLatestFrom(this.store.select(groupsFeature.selectGroupsState)),
            exhaustMap(([{ groupId, userIds }, state]) => {
                const group = state.groups.find(
                    (current) => current.gmvid === groupId
                );
                if (!group) return of(GroupsActions.mutationFailed());
                const admins = Array.from(
                    new Set([
                        ...state.admins.map((admin) => admin.gmvid),
                        ...userIds,
                    ])
                );
                return this.api.updateGroup({ ...group, admins }).pipe(
                    map(() =>
                        GroupsActions.peopleMutationSucceeded({ groupId })
                    ),
                    catchError(() => of(GroupsActions.mutationFailed()))
                );
            })
        )
    );

    readonly removeAdmins$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GroupsActions.removeAdminsRequested),
            withLatestFrom(this.store.select(groupsFeature.selectGroupsState)),
            exhaustMap(([{ groupId, usernames }, state]) => {
                const group = state.groups.find(
                    (current) => current.gmvid === groupId
                );
                if (!group) return of(GroupsActions.mutationFailed());
                const admins = state.admins
                    .filter((admin) => !usernames.includes(admin.username))
                    .map((admin) => admin.gmvid);
                return this.api.updateGroup({ ...group, admins }).pipe(
                    map(() =>
                        GroupsActions.peopleMutationSucceeded({ groupId })
                    ),
                    catchError(() => of(GroupsActions.mutationFailed()))
                );
            })
        )
    );

    readonly reloadPeople$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GroupsActions.peopleMutationSucceeded),
            map(({ groupId }) => GroupsActions.groupSelected({ groupId }))
        )
    );
}
